import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Teacher's class activity digest for one classroom (ai/plans/dynamic-home-gap-analysis.md 2.1), e.g.
 * "14 of 28 students active this week · Diagnostics: 12 students, avg 68%".
 *
 * Data comes from test-prep-iii's GetClassroomActivitySummary, mounted on this origin at /internal/tp3/ and
 * authenticated by the same session cookie. It answers 403 unless the viewer owns the classroom. The digest
 * is a supplement to the classroom card, so loading, errors and 403s all render nothing.
 */
export const activitySummaryUrl = classroomUuid => '/internal/tp3/Ajax/Ajax.php?action=GetClassroomActivitySummary' +
	'&classroomUuid=' + encodeURIComponent(classroomUuid);

// The title box is a fixed 188px on desktop; the most important types come first
const maxTypes = 3;

export const typeLabels = {
	diagnostic: 'Diagnostics',
	practice: 'Practice exams',
	quiz: 'Quizzes',
	reading: 'Readings',
	video: 'Videos',
	other: 'Other activities'
};

const ClassroomActivityCard = (props) => {
	const { classroomUuid } = props;
	const [summary, setSummary] = useState(null);

	/**
	 * Fetch the digest once per classroom; abort if the card unmounts first
	 */
	useEffect(() => {
		const controller = new AbortController();

		fetch(activitySummaryUrl(classroomUuid), { credentials: 'same-origin', signal: controller.signal })
			.then(response => response.ok ? response.json() : null)
			.then(data => setSummary(data && data.payload ? data.payload : null))
			.catch(error => {
				if (error.name !== 'AbortError') {
					setSummary(null);
				}
			});

		return () => controller.abort();
	}, [classroomUuid]);

	if (summary === null) {
		return null;
	}

	const period = summary.days === 7 ? 'this week' : 'in the last ' + summary.days + ' days';

	return (
		<div className={'classroom-activity'}>
			<p className={'classroom-activity-headline'}>
				{summary.activeStudents} of {summary.rosterSize} students active {period}
			</p>
			{summary.byType.length > 0 &&
				<ul className={'classroom-activity-types'}>
					{summary.byType.slice(0, maxTypes).map(row =>
						<li key={row.type}>
							{typeLabels[row.type] || row.type}: {row.students} {row.students === 1 ? 'student' : 'students'}
							{row.averagePercent !== null && <>, avg {row.averagePercent}%</>}
						</li>
					)}
				</ul>
			}
		</div>
	);
};

export default ClassroomActivityCard;

ClassroomActivityCard.propTypes = {
	classroomUuid: PropTypes.string.isRequired
};
